import {StyleSheet} from "aphrodite";
import {type ReactNode} from "react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";

import type {JsonPath} from "../../types.ts";
import {isPlaceholder} from "../../utils.ts";
import ListNode from "../document/list-node.tsx";
import ParagraphNode from "../document/paragraph-node.tsx";
import PlaceholderBlock from "../placeholder-block.tsx";

import type {CellData} from "./types.ts";

const StyledTd = addStyle("td");
const StyledTh = addStyle("th");

interface Props {
    kind: CellData["kind"];
    content: CellData["content"];
    path: JsonPath;
}

export default function CellNode({kind, content, path}: Props) {
    const contentPath = [...path, "content"];

    let contentNode: ReactNode = null;
    if (isPlaceholder(content)) {
        contentNode = (
            <PlaceholderBlock placeholderKey={content.$placeholder} />
        );
    } else if (content.kind === "paragraph") {
        contentNode = <ParagraphNode text={content.text} path={contentPath} />;
    } else if (
        content.kind === "bulleted-list" ||
        content.kind === "numbered-list"
    ) {
        contentNode = (
            <ListNode
                kind={content.kind}
                items={content.items}
                path={contentPath}
            />
        );
    }

    if (kind === "th") {
        return <StyledTh style={styles.th}>{contentNode}</StyledTh>;
    }

    return <StyledTd style={styles.td}>{contentNode}</StyledTd>;
}

const styles = StyleSheet.create({
    td: {
        display: "table-cell",
        border: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
        padding: sizing.size_080,
    },
    th: {
        display: "table-cell",
        border: `${border.width.thin} solid ${semanticColor.core.border.neutral.subtle}`,
        padding: sizing.size_080,
        background: semanticColor.surface.secondary,
        fontWeight: "bold",
    },
});
